
import { Component, Emit, InjectReactive, Prop, Vue, Watch } from 'vue-property-decorator';

import DirectionsSvg from '@/components/DirectionsSvg.vue';
import PhoneSvg from '@/components/PhoneSvg.vue';
import { OrderMethods } from '@/enums';
import UserModule from '@/store/modules/user';
import { dayToIndexMap, formatHour, formatPhoneNumber, hourMethodMap } from '@/utils/global.util';

@Component({
  components: {
    DirectionsSvg,
    PhoneSvg,
  }
})
export default class LocationInfo extends Vue {
  @Prop({ type: Number, default: null }) id: number;
  @Prop({ type: String, default: '' }) name: string;
  @Prop({ type: String, default: '' }) slug: string;
  @Prop({ type: Number, default: null }) distanceFromUser: number;
  @Prop({ type: String, default: '' }) phoneNumber: string;
  @Prop({ type: String, default: '' }) street: string;
  @Prop({ type: String, default: '' }) city: string;
  @Prop({ type: String, default: '' }) state: string;
  @Prop({ type: String, default: '' }) zip: string;
  @Prop({ type: Boolean, default: false }) supportsPickup: boolean;
  @Prop({ type: Boolean, default: false }) supportsDelivery: boolean;
  @Prop({ type: Boolean, default: false }) supportsCurbside: boolean;
  @Prop({ type: Boolean, default: false }) supportsTransfers: boolean;
  @Prop({ type: Boolean, default: false }) supportsGroupOrders: boolean;

  @Prop({ type: Number, default: null }) latitude: number;
  @Prop({ type: Number, default: null }) longitude: number;
  @Prop({ type: Number, default: null }) leadHours: number;
  @Prop({ type: Boolean, default: false }) showSpecialInstructions: boolean;
  @Prop({ type: Number, default: null }) specialInstructionsMaxLength: number;
  @Prop({ type: Boolean, default: false }) supportsCoupons: boolean;
  @Prop({ type: Number, default: null }) advanceOrderDays: number;
  @Prop({ type: Number, default: null }) asapOrderCutOffMinutesBeforeClosing: number;
  @Prop({ type: String, default: '' }) customerFacingMessage: string;

  @Prop({ type: Array, default: () => [] }) billingMethods: string[];
  @Prop({ type: Array, default: () => [] }) cardsAccepted: string[];
  @Prop({ type: Array, default: () => [] }) hours: Period[];
  @Prop({ type: Array, default: () => [] }) overrideHours: DatePeriod[];
  @Prop({ type: Array, default: () => [] }) attributes: string[];
  @Prop({ type: Boolean, default: false }) hideSelectStoreButton: boolean;
  @Prop({ type: Boolean, default: false }) supportsOnlineOrdering: boolean;
  @Prop({ type: Boolean, default: false }) orderingDisabled: boolean;
  @Prop({ type: String, default: null }) specialInstructionsHelpText: string;
  @Prop({ type: String, default: null }) regionId: string;

  @InjectReactive('currentOrderMethod') currentOrderMethod: OrderMethods;

  todaysIndex: number;
  dayMap = dayToIndexMap;

  isFavorite: boolean;
  isDefault: boolean;

  data() {
    return {
      todaysIndex: new Date().getDay(),
      isFavorite: false,
      isDefault: false
    }
  }

  mounted() {
    this.handlefavoriteRestaurantsChange();
  }

  @Emit('set-location')
  selectLocation() {
    return this.$props;
  }

  @Emit('hide-info')
  hideInfo() {
    return null;
  }

  get isLoggedIn() {
    return UserModule.loggedIn;
  }

  get favoriteRestaurants() {
    return UserModule.favoriteRestaurants || {};
  }

  @Watch('favoriteRestaurants')
  handlefavoriteRestaurantsChange() {
    const store = this.favoriteRestaurants.find((restaurant) => restaurant.id === this.id);
    if (store) {
      this.isFavorite = true;
      this.isDefault = store.isDefault;
    } else {
      this.isFavorite = false;
      this.isDefault = false;
    }
  }

  get dashedPhoneNumber() {
    return formatPhoneNumber(this.phoneNumber);
  }

  get telephoneRef() {
    return `tel:${this.dashedPhoneNumber}`;
  }

  get addressRef(): string {
    // return `http://maps.google.com/?q=${this.latitude},${this.longitude}+(${this.slug})`;
    return `http://google.com/maps/place/${this.street},${this.city},${this.state},${this.zip}`;
  }

  get hoursForMethod(): Period[] {
    // TODO~> concider overridehours here.
    // ex holiday hours, etc. show that its an override in the UI
    return this.hours
      ?.filter((hour) => hour.type === hourMethodMap.get(this.currentOrderMethod || OrderMethods.PICKUP))
      ?.map((hour: Period) => {
        return {
          ...hour,
          to: formatHour(hour.to),
          from: formatHour(hour.from),
        }
      }) || [];
  }

  get todaysHours() {
    return this.hoursForMethod.find((hour) => this.dayMap.get(hour.day) === this.todaysIndex) || {};
  }

  get nextOpenDayHours() {
    for (let iterator = 1; iterator <= 7; iterator++) {
      const dayHours = this.hoursForMethod.find((hour) => this.dayMap.get(hour.day) === this.todaysIndex + iterator);
      if (dayHours) {
        return dayHours;
      }
    }
    return null;
  }

  get hoursSortedByDay() {
    const sorted = this.hoursForMethod.sort((a, b) => {
      return (this.dayMap.get(a.day) as number) - (this.dayMap.get(b.day) as number);
    });

    const firstHalf = sorted.slice(this.todaysIndex).map((day, index) => {
      if (index === 0) { day.day = 'today' }
      if (index === 1) { day.day = 'tomorrow' }
      return day;
    });
    const secondHalf = sorted.slice(0, this.todaysIndex);
    return [...firstHalf, ...secondHalf];
  }

  get supportsCurrentMethod() {
    return this[`supports${this.currentOrderMethod?.capitalize()}`];
  }

  async submitMakeFavorite() {
    try {
      const favoriteModel = await UserModule.addFavoriteLocation({ restaurantId: this.id, isDefault: false }) as FavoriteLocationModel;
      if (favoriteModel) {
        const addedLocation: FavoriteRestaurant = {
          id: this.id,
          name: this.name,
          slug: this.slug,
          street: this.street,
          city: this.city,
          state: this.state,
          zip: this.zip,
          phoneNumber: this.phoneNumber,
          supportsPickup: this.supportsPickup,
          supportsDelivery: this.supportsDelivery,
          supportsCurbside: this.supportsCurbside,
          supportsTransfers: this.supportsTransfers,
          distanceFromUser: this.distanceFromUser,
          latitude: this.latitude,
          longitude: this.longitude,
          leadHours: this.leadHours,
          showSpecialInstructions: this.showSpecialInstructions,
          specialInstructionsMaxLength: this.specialInstructionsMaxLength,
          supportsCoupons: this.supportsCoupons,
          advanceOrderDays: this.advanceOrderDays,
          asapOrderCutOffMinutesBeforeClosing: this.asapOrderCutOffMinutesBeforeClosing,
          customerFacingMessage: this.customerFacingMessage,
          billingMethods: this.billingMethods,
          cardsAccepted: this.cardsAccepted,
          hours: this.hours,
          overrideHours: this.overrideHours,
          attributes: this.attributes,
          isDefault: favoriteModel.isDefault,
          favoriteId: favoriteModel.id,
          specialInstructionsHelpText: this.specialInstructionsHelpText,
          regionId: this.regionId,
          supportsOnlineOrdering: this.supportsOnlineOrdering,
          orderingDisabled: this.orderingDisabled,
        };
        UserModule.addFavoriteLocationToCustomer(addedLocation);
      }
    } catch (err) {
      this.$log.error(err);
      this.$toasted.error(err.data);
    }
  }

  async submitMakeDefault() {
    try {
      const favoriteModel = await UserModule.addFavoriteLocation({ restaurantId: this.id, isDefault: true }) as FavoriteLocationModel;
      if (favoriteModel) {
        const addedLocation: FavoriteRestaurant = {
          id: this.id,
          name: this.name,
          slug: this.slug,
          street: this.street,
          city: this.city,
          state: this.state,
          zip: this.zip,
          phoneNumber: this.phoneNumber,
          supportsPickup: this.supportsPickup,
          supportsDelivery: this.supportsDelivery,
          supportsCurbside: this.supportsCurbside,
          supportsTransfers: this.supportsTransfers,
          distanceFromUser: this.distanceFromUser,
          latitude: this.latitude,
          longitude: this.longitude,
          leadHours: this.leadHours,
          showSpecialInstructions: this.showSpecialInstructions,
          specialInstructionsMaxLength: this.specialInstructionsMaxLength,
          supportsCoupons: this.supportsCoupons,
          advanceOrderDays: this.advanceOrderDays,
          asapOrderCutOffMinutesBeforeClosing: this.asapOrderCutOffMinutesBeforeClosing,
          customerFacingMessage: this.customerFacingMessage,
          billingMethods: this.billingMethods,
          cardsAccepted: this.cardsAccepted,
          hours: this.hours,
          overrideHours: this.overrideHours,
          attributes: this.attributes,
          isDefault: favoriteModel.isDefault,
          favoriteId: favoriteModel.id,
          specialInstructionsHelpText: this.specialInstructionsHelpText,
          regionId: this.regionId,
          supportsOnlineOrdering: this.supportsOnlineOrdering,
          orderingDisabled: this.orderingDisabled,
        };
        UserModule.addFavoriteLocationToCustomer(addedLocation);
      }
    } catch (err) {
      this.$log.error(err);
      this.$toasted.error(err.data);
    }
  }

  async submitRemoveFromFavorites() {
    const favoriteRestaurant = UserModule.favoriteRestaurants.find((restaurant) => restaurant.id === this.id);
    if (favoriteRestaurant) {
      try {
        if (favoriteRestaurant) {
          UserModule.removeFavoriteLocationFromCustomer(favoriteRestaurant);
          await UserModule.removeFavoriteLocation(favoriteRestaurant.favoriteId);
        }
      } catch (err) {
        UserModule.addFavoriteLocationToCustomer(favoriteRestaurant);
        this.$log.error(err);
        this.$toasted.error(err.data);
      }
    }
  }
}
